// ---------------------------------------------------------------------------
// Timezone helpers for the browser: pick a zone, default sensibly, and
// display an already-computed UTC timestamp in one.
//
// The backend is the sole authority for turning a date + time + timezone into
// a UTC instant (api/src/core/timezone.ts) — this module never does that
// arithmetic itself, including the DST-gap/ambiguity checks. A scheduling
// mistake (nonexistent or ambiguous local time, a past time) comes back as a
// normal validation error from the API and is shown the same way every other
// form error already is in this app.
// ---------------------------------------------------------------------------

const FALLBACK_ZONES = [
  'UTC',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Australia/Melbourne',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
];

/** Every IANA zone this browser knows about. Falls back to a small curated list on a browser without Intl.supportedValuesOf. */
export function listTimezones() {
  if (typeof Intl.supportedValuesOf === 'function') {
    const zones = new Set(Intl.supportedValuesOf('timeZone'));
    zones.add('UTC'); // some engines omit this specific link name from the list despite accepting it
    return [...zones].sort();
  }
  return FALLBACK_ZONES;
}

/**
 * The browser's own timezone — used only as the starting selection an
 * operator can freely change, per "do not silently assume UTC" and "no admin-
 * profile timezone exists in this portal to prefer instead". Never used to
 * interpret a date/time itself; that always goes to the server with an
 * explicit IANA id.
 */
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** "Australia/Sydney (UTC+11:00)" — a live current offset, not a static/possibly-stale abbreviation table. */
export function timezoneLabel(timeZone, at = new Date()) {
  const name = timeZone.replace(/_/g, ' ');
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' }).formatToParts(at);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offset ? `${name} (${offset})` : name;
  } catch {
    return name;
  }
}

/**
 * Break an already-known UTC instant into its wall-clock date/time parts
 * inside `timeZone` — for pre-filling a form from a value the server already
 * computed, e.g. re-opening the trial-dates form for an org that was created
 * with an exact trial start/end time. This is the safe, unambiguous direction
 * (instant -> wall clock); the reverse (wall clock -> instant, which can be
 * invalid or ambiguous across a DST transition) is never done in the browser
 * — see this module's file-level comment.
 */
export function wallPartsInZone(iso, timeZone) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const parts = {};
  for (const p of formatted) {
    if (p.type !== 'literal') parts[p.type] = p.value;
  }
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/** Format a UTC ISO timestamp for display inside `timeZone`. en-GB/24h, matching dom.js's fmtDate elsewhere in this app. */
export function fmtInZone(iso, timeZone) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
